import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import globalDyHandler from 'core/utils/helpers/dyHandler';
import UrlParse from 'url-parse';
import Cookies from 'js-cookie';
import { getEmailPopupCookie } from 'core/utils/helpers/emailPopupCookie';
import { selectEmailPopup, selectUserIsIdentified } from 'core/selectors/user';
import { selectEmailOptInSubscribeModalConfig } from 'core/modules/Modals/EmailOptInSubscribeModal/selectors';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import Onboarding from 'core/modules/Onboarding/Onboarding';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import { useModal } from 'core/modules/Modal/ModalProvider';

import {
  selectOnboardingIsEnabled,
  selectOnboardingSuppressedPages,
  selectHasEmailOptInPopupEnabled,
} from 'core/selectors/app';

const forceCookieName = 'showOnboarding';
const forceParamName = 'showOnboarding';

function EmailOptInTrigger() {
  const location = useLocation();
  const { openModal } = useModal();

  const isUserIdentified = useSelector(selectUserIsIdentified);
  const userState = useSelector(selectEmailPopup);
  const subscribeModalConfig = useSelector(selectEmailOptInSubscribeModalConfig);
  const emailOptInPopupEnabled = useSelector(selectHasEmailOptInPopupEnabled);
  const onboardingIsEnabled = useSelector(selectOnboardingIsEnabled);
  const onboardingSuppressedPages = useSelector(selectOnboardingSuppressedPages);
  const isMobileMQ = useSelector(selectIsMobileMQ);

  const isSuppressedPage = () => {
    for (let i = 0; i < onboardingSuppressedPages.length; i += 1) {
      if (location.pathname.indexOf(onboardingSuppressedPages[i]) !== -1) {
        return true;
      }
    }
    return false;
  };

  const hasFCookieTrue = () => Cookies.get('f') === 'true';

  /*
  Users are eligible for onboarding flow if they are logged in and:
  1) first time visitors
  2) forced into the flow via query param flag
  3) forced into the flow via cookie
  4) forced into the flow from outside (e.g., Dynamic Yield)
  */
  const isLoggedInFirstTimeUser = () => {
    const emailPopupCookie = getEmailPopupCookie();
    return hasFCookieTrue() && isUserIdentified && emailPopupCookie && emailPopupCookie.seenCount === 0;
  };

  const isForcedOnboarding = () => {
    const shouldShowOnboarding = new UrlParse(location.search, {}, true).query[forceParamName];
    return isUserIdentified && !!shouldShowOnboarding;
  };

  const isForcedOnboardingByCookie = () =>
    isUserIdentified && Cookies.get(forceCookieName) && Cookies.get(forceCookieName) !== 'null';

  const checkOtherModalIsOpen = () => document.documentElement.classList.contains('mn_hasModal');

  const shouldDisplayOnboarding = () =>
    onboardingIsEnabled && !isSuppressedPage() && !checkOtherModalIsOpen()
    && !globalDyHandler.isOnboardingSuspended // Can be changed outside from the DY
    && (isLoggedInFirstTimeUser() || isForcedOnboarding() || isForcedOnboardingByCookie());

  const [showOnboarding, setShowOnboarding] = useState(() => shouldDisplayOnboarding());

  const forceOnboardingFromOutside = () => isUserIdentified && !checkOtherModalIsOpen() && setShowOnboarding(true);

  const shouldDisplaySubscribePopup = () => {
    // Modal could be disabled on page level config
    const { checkIsDisabled } = subscribeModalConfig;
    const isDisabled = checkIsDisabled(isMobileMQ);
    const { showPopup, hasSeen } = userState;

    return (
      showPopup && !hasSeen && !isDisabled &&
      emailOptInPopupEnabled && !globalDyHandler.isEmailOptInPopupSuspended
    );
  };

  const shouldDisplaySuccessPopup = () => {
    const { hasSeen, showPopup } = userState;
    const { search } = location;

    // After successful submitting the 'Email optin subscribe' form user will be redirected to 'cbUrl' page
    // which has the same url but with 'act=email' parameter
    const isOnSubscribeFormCbUrlPage = search.indexOf('act=email') > -1;

    // Show popup if the user was supposed to.
    // This showPopup is related to subscribe popup, so in this case we expect to have it false value.
    return !showPopup && hasSeen && isOnSubscribeFormCbUrlPage;
  };

  useEffect(() => {
    globalDyHandler.forceOnboardingFromOutside = forceOnboardingFromOutside;
  }, [forceOnboardingFromOutside]);

  useEffect(() => {
    if (showOnboarding) {
      document.documentElement.setAttribute('data-show-onboarding', true);
    }
  }, [showOnboarding]);

  useEffect(() => {
    if (!isUserIdentified) {
      return;
    }

    if (!showOnboarding) {
      if (shouldDisplaySuccessPopup()) {
        openModal({ name: 'mt_emailOptInSuccess' });
      } else if (shouldDisplaySubscribePopup()) {
        openModal({ name: 'mt_emailOptInSubscribe' });
      }
    }
  }, []);

  return showOnboarding ? <Onboarding /> : null;
}

export default (props) => <ComponentErrorBoundary><EmailOptInTrigger {...props} /></ComponentErrorBoundary>;
